import {
  DataGrid as FluentDataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Text,
  createTableColumn,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { TableColumnDefinition, TableRowId } from '@fluentui/react-components';
import type { ReactNode } from 'react';

/** Declarative column for the generic resource grid. */
export interface ResourceColumn<T> {
  /** Stable column id. */
  columnId: string;
  /** Header content. */
  header: ReactNode;
  /** Cell content for a given row. */
  renderCell: (item: T) => ReactNode;
  /** Optional fixed/min width hint in px. */
  minWidth?: number;
}

export interface DataGridProps<T> {
  /** Column definitions, in display order. */
  columns: ReadonlyArray<ResourceColumn<T>>;
  /** Row data. */
  rows: readonly T[];
  /** Stable, unique id per row (used as the React key and row id). */
  getRowId: (item: T) => string;
  /** Invoked when a row is activated (click / Enter). */
  onRowClick?: (item: T) => void;
  /** Message shown when there are no rows. */
  emptyMessage?: string;
}

const useStyles = makeStyles({
  clickableRow: {
    cursor: 'pointer',
  },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Generic Azure-portal resource-list grid over Fluent's {@link FluentDataGrid}.
 * Callers supply typed columns + rows and an optional row-click handler (used
 * to open a detail blade or navigate). Shared by the Dashboard's recent-tenants
 * tile (Dev-C) and Dev-D's tenants/users lists.
 */
export function DataGrid<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyMessage = 'No items to display.',
}: DataGridProps<T>): JSX.Element {
  const styles = useStyles();

  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        <Text>{emptyMessage}</Text>
      </div>
    );
  }

  const columnDefs: Array<TableColumnDefinition<T>> = columns.map((col) =>
    createTableColumn<T>({
      columnId: col.columnId,
      renderHeaderCell: () => col.header,
      renderCell: (item) => col.renderCell(item),
    })
  );

  const resolveRowId = (item: T): TableRowId => getRowId(item);

  return (
    <FluentDataGrid
      items={rows as T[]}
      columns={columnDefs}
      getRowId={resolveRowId}
      focusMode="row_unstable"
      size="medium"
    >
      <DataGridHeader>
        <DataGridRow>
          {({ renderHeaderCell }): JSX.Element => (
            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
          )}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody<T>>
        {({ item, rowId }): JSX.Element => (
          <DataGridRow<T>
            key={rowId}
            className={onRowClick !== undefined ? styles.clickableRow : undefined}
            onClick={onRowClick === undefined ? undefined : () => onRowClick(item)}
          >
            {({ renderCell }): JSX.Element => <DataGridCell>{renderCell(item)}</DataGridCell>}
          </DataGridRow>
        )}
      </DataGridBody>
    </FluentDataGrid>
  );
}
